/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import type {
  Coordinates,
  PlotPreset,
  SourceSelection,
  TimeSeriesService,
} from '@opengeoweb/shared';
import type { mapTypes } from '@opengeoweb/store';
import type { ConfigurableMapConnectProps } from '../ConfigurableMapConnect';
import { ConfigurableMapConnect } from '../ConfigurableMapConnect';
import type { InitialHarmTempAndPrecipProps } from '../MultiMapViewConnect/HarmoniePresets';
import { HarmonieTempAndPrecipPreset } from '../MultiMapViewConnect/HarmoniePresets';
import type { ModelRunIntervalProps } from '../MultiMapViewConnect/ModelRunInterval';
import { ModelRunInterval } from '../MultiMapViewConnect/ModelRunInterval';
import type { MultiMapPreset } from '../MultiMapViewConnect/MultiMapViewConnect';
import { MultiMapViewConnect } from '../MultiMapViewConnect/MultiMapViewConnect';
import { TimeSliderConnect } from '../TimeSliderConnect';
// Theres copies of these interfaces in libs/store/src/store/map/map/types.ts
export interface InitialMultiMapProps {
  mapPreset: mapTypes.MapPreset[];
  syncGroupsIds: string[];
  shouldShowZoomControls?: boolean;
}

export interface InitialSliderProps {
  sliderPreset: { mapId: string };
  syncGroupsIds: string[];
}

export interface InitialMapProps {
  mapPreset: mapTypes.MapPreset;
  syncGroupsIds: string[];
  mapControls?: React.ReactNode;
  shouldDisplayDrawControls?: boolean;
  displaySearchButtonInMap?: boolean;
  displayDataExplorerButtonOnMap?: boolean;
}

// A copy of this type exists in libs/time-series/src/lib/components/ComponentsLookUp/componentsLookUp.tsx
export interface InitialTimeSeriesProps {
  plotPreset: PlotPreset;
  services: TimeSeriesService[];
  hideLocationMarkers?: boolean;
  syncGroupsIds: string[];
}

export interface InitialSoundingsProps {
  selectedSources: Record<string, SourceSelection>;
  selectedLocations: Record<string, string | Coordinates | undefined>;
  selectedTime?: string;
  diagramType?: 'SKEWT' | 'BIJVOET' | 'EMAGRAM';
  syncGroupsIds: string[];
}

export interface InitialPluginProps {
  id: string;
  name: string;
  url: string;
  author?: { name?: string };
  version?: string;
  description?: string;
  license?: string;
}

export type InitialProps =
  | InitialMapProps
  | InitialMultiMapProps
  | InitialHarmTempAndPrecipProps
  | ModelRunIntervalProps
  | mapTypes.MapPresetInitialProps
  | InitialSliderProps
  | InitialTimeSeriesProps
  | InitialSoundingsProps
  | InitialPluginProps
  | Record<string, unknown>;

/**
 * The lookup table is for registering your own components with the workspace.
 * @param payload
 * @returns
 */

export type SupportedComponentTypes =
  | 'Map'
  | 'MultiMap'
  | 'ModelRunInterval'
  | 'HarmonieTempAndPrecipPreset'
  | 'TimeSlider';
export interface ComponentsLookUpPayload {
  componentType: SupportedComponentTypes;
  id: string;
  title?: string;
  initialProps: InitialProps;
}

export const componentsLookUp = ({
  componentType,
  initialProps,
  id,
}: ComponentsLookUpPayload): React.ReactElement => {
  switch (componentType) {
    case 'Map': {
      const {
        mapPreset,
        mapControls,
        shouldDisplayDrawControls,
        displaySearchButtonInMap,
        displayDataExplorerButtonOnMap,
      } = initialProps as InitialMapProps;
      return (
        <ConfigurableMapConnect
          {...(mapPreset as ConfigurableMapConnectProps)}
          data-testid="coreConfigurableMapConnect"
          displayDataExplorerButtonOnMap={displayDataExplorerButtonOnMap}
          displayGetFeatureInfoButtonInMap
          displaySearchButtonInMap={displaySearchButtonInMap}
          id={id}
          initialBbox={mapPreset?.proj?.bbox}
          mapControls={mapControls}
          shouldDisplayDrawControls={shouldDisplayDrawControls}
          shouldDisplayMeasureToolButton
          srs={mapPreset?.proj?.srs}
        />
      );
    }
    case 'MultiMap': {
      const { mapPreset, shouldShowZoomControls, syncGroupsIds } =
        initialProps as InitialMultiMapProps;

      if (!mapPreset || mapPreset.length === 0) {
        return null!;
      }

      return (
        <MultiMapViewConnect
          cols={2}
          data-testid="coreMultiMapViewConnect"
          maps={mapPreset.map((preset, index) => {
            const { layers, autoUpdateLayerId, autoTimeStepLayerId } = preset;
            return {
              id: `${id}_${index}`,
              syncGroupsIds,
              layers,
              autoUpdateLayerId,
              autoTimeStepLayerId,
              bbox: mapPreset[0]?.proj?.bbox,
              srs: mapPreset[0]?.proj?.srs,
              componentType: 'MultiMap',
            } as MultiMapPreset;
          })}
          rows={2}
          showZoomControls={shouldShowZoomControls}
        />
      );
    }
    case 'HarmonieTempAndPrecipPreset': {
      const { layers } = initialProps as InitialHarmTempAndPrecipProps;
      return (
        <HarmonieTempAndPrecipPreset
          data-testid="coreHarmonieTempAndPrecipPreset"
          layers={layers || null}
        />
      );
    }
    case 'ModelRunInterval': {
      return (
        <ModelRunInterval
          {...(initialProps as ModelRunIntervalProps)}
          data-testid="coreModelRunInterval"
        />
      );
    }

    case 'TimeSlider': {
      const { sliderPreset } = initialProps as InitialSliderProps;
      return (
        <TimeSliderConnect
          {...sliderPreset}
          data-testid="coreTimeSliderConnect"
          sourceId={id}
        />
      );
    }

    default:
      return null!;
  }
};
